import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app";
import selectConfigMap from "../selectors/configMapSelector";
import selectCategorizationAnnotations from "../../metadata/selectors/selectCategorizationAnnotations";
import selectAnnotationReducer from "../../annotation/selectors/annotationReducerSelector";
import { selectEntityTypeCategorizations } from "../selectors/categorizationLinkSelectors";
import { getInLanguageOrDefault } from "../../common/utilities/language";
import { EntityNormalization } from "../../annotator/interfaces/entity";
import {
  ConversionDisplay,
  ConversionMeta,
  EntityConversions,
  EntityLink,
} from "../interfaces/conversion";
import {
  buildConfigInputs,
  buildStepMetas,
  isCustomerMethod,
  resolveIsCustomer,
  usesCategoryContext,
} from "../utils/conversionMeta";

// Resolves an entity's conversions into ordered display steps, each with the
// inputs (config categorizations, entity link) it consumes.
const useEntityConversions = (
  entityTypeId: string | undefined,
  entityNormalizations: Array<EntityNormalization> | null | undefined,
  entityLink?: EntityLink,
): EntityConversions => {
  const {
    t,
    i18n: { language },
  } = useTranslation("detail");
  const configMap = useAppSelector(selectConfigMap);
  const categorizationAnnotations = useAppSelector(
    selectCategorizationAnnotations,
  );

  // Runtime signal: an annotation on this entity carries a customer method.
  const hasCustomerMethod = useAppSelector((state) => {
    if (!entityTypeId) {
      return false;
    }
    return selectAnnotationReducer(state).some(
      (annotation) =>
        annotation.entity?.id === entityTypeId &&
        isCustomerMethod(
          annotation.entityAnnotationNormalization?.normalizedMethod,
        ),
    );
  });

  const sortedNormalizations = (entityNormalizations ?? [])
    .slice()
    .sort((a, b) => a.index - b.index);

  const isCustomer = resolveIsCustomer(
    sortedNormalizations,
    entityTypeId,
    hasCustomerMethod,
  );
  const stepMetas = buildStepMetas(sortedNormalizations, isCustomer);

  // An admin-authored description wins over the type-derived default.
  const describeDetail = (conversion: ConversionMeta): string => {
    if (conversion.description) {
      const authored = getInLanguageOrDefault(
        conversion.description,
        language,
        "",
      );
      if (authored) {
        return authored;
      }
    }
    const base = `${conversion.base}.${conversion.key}`;
    if (conversion.hasFormat) {
      return conversion.format
        ? t(`${base}.detail`, { format: conversion.format })
        : t(`${base}.detailNoFormat`);
    }
    return t(`${base}.detail`);
  };

  const conversions: Array<ConversionDisplay> = stepMetas.map(({ meta }) => ({
    icon: meta.icon,
    title: t(`${meta.base}.${meta.key}.title`),
    detail: describeDetail(meta),
    inputs: [],
  }));

  // Config inputs attach to the category-driven step (else the first).
  if (usesCategoryContext(sortedNormalizations, isCustomer) && entityTypeId) {
    const configInputs = buildConfigInputs(
      selectEntityTypeCategorizations(configMap, entityTypeId),
      categorizationAnnotations,
      language,
      t("conversion.notSet"),
    );
    if (configInputs.length > 0 && conversions.length > 0) {
      const categoryDrivenIndex = stepMetas.findIndex(
        (step) => step.categoryDriven,
      );
      const targetIndex = categoryDrivenIndex === -1 ? 0 : categoryDrivenIndex;
      conversions[targetIndex].inputs.push(...configInputs);
    }
  }

  // The entity dependency attaches to the final step.
  if (entityLink && entityLink.names.length > 0 && conversions.length > 0) {
    conversions[conversions.length - 1].inputs.push({
      kind: entityLink.direction,
      label: t(`conversion.${entityLink.direction}`),
      value: entityLink.names.join(" · "),
    });
  }

  return {
    entityLabel: t("conversion.entityLabel"),
    header: t("conversion.activeNormalizations"),
    conversions,
  };
};

export default useEntityConversions;
